import { Component, computed, effect, inject, input, output, signal } from "@angular/core";
import { NgbAccordionModule } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";

import { AdaaHelper } from "../../../../../../../core/utils";
import { Constants } from "../../../../../../constants/constants";
import { Language } from "../../../../../../constants/enums";
import type { PropTypeModelType, WorkflowDiffVersionType } from "../../../../../../models";
import { LanguageService } from "../../../../../../services";
import {
  AccordionSectionConfigType,
  CompositeSectionConfigType,
  ListSectionConfigType,
  type ScreenSectionConfigType,
  type WorkflowDiffListItemType,
} from "../../wf-difference.types";
import { ListDiffViewComponent } from "../list-diff-view/list-diff-view";
import { NormalDiffViewComponent } from "../normal-diff-view/normal-diff-view";
import { TargetsDiffViewComponent } from "../targets-diff-view/targets-diff-view";

@Component({
  selector: "adaa-accordion-diff-view",
  imports: [
    TranslatePipe,
    NormalDiffViewComponent,
    ListDiffViewComponent,
    NgbAccordionModule,
    TargetsDiffViewComponent,
  ],
  templateUrl: "./accordion-diff-view.html",
  styleUrl: "./accordion-diff-view.scss",
})
export class AccordionDiffViewComponent {
  readonly languageService = inject(LanguageService);

  showOnlyNew = input.required<boolean>();
  config = input.required<AccordionSectionConfigType>();
  isNew = input<boolean>(false);
  newData = input<WorkflowDiffVersionType>();
  oldData = input<WorkflowDiffVersionType>();
  props = input<PropTypeModelType[]>([]);
  showDIffOnly = input<boolean>(false);

  Language = Language;
  displayLabel = computed(() => {
    const cfg = this.config();
    return this.languageService.translate(cfg.derivedLabel?.(this.newData()!) ?? cfg.label);
  });

  newList = computed(() => {
    const conf = this.config();
    const data = this.newData();

    if (!data || !conf || !AdaaHelper.isDefined(data?.[conf.field])) return [];
    const list = data?.[conf.field];
    if (!Array.isArray(list)) return [];
    return list as WorkflowDiffListItemType[];
  });

  oldList = computed(() => {
    const conf = this.config();
    const data = this.oldData();

    if (!data || !conf || !AdaaHelper.isDefined(data?.[conf.field])) return [];
    const list = data?.[conf.field];
    if (!Array.isArray(list)) return [];
    return list as WorkflowDiffListItemType[];
  });

  data = computed(() => {
    const res = this._dataProcessor(this.newList(), this.oldList());
    if (res.some((item) => item.hasDiff)) {
      const parentId = this.#parentId();
      if (parentId != null) {
        this.emitHasDiff.emit({ hasDiff: true, parentId });
      }
    }
    return res;
  });
  hasAnyDiff = computed(() => {
    const data = this.data();
    return data.some((item) => item.hasDiff);
  });

  emitHasDiff = output<{ hasDiff: boolean; parentId: number }>();
  parentConf = input<WorkflowDiffListItemType>();
  childHasDiff = signal<boolean>(false);
  parentDiffId = signal<number | undefined>(undefined);

  readonly $asAccordionConfig = ($s: CompositeSectionConfigType) => $s as AccordionSectionConfigType;
  readonly $asListConfig = ($s: CompositeSectionConfigType) => $s as ListSectionConfigType;

  constructor() {
    effect(() => {
      this._fetchConstants();
    });
  }

  #parentId() {
    const id = this.parentConf()?.id;
    if (typeof id !== "number") return undefined;
    return id;
  }

  private _dataProcessor($new: WorkflowDiffListItemType[], $old: WorkflowDiffListItemType[]) {
    const conf = this.config();
    const id = conf.idKey;
    const output = [];

    if (conf.derivedList && typeof conf.derivedList === "function") {
      $new = conf.derivedList($new) as WorkflowDiffListItemType[];
      $old = conf.derivedList($old) as WorkflowDiffListItemType[];
    }

    // NOTE: removed team member
    for (const item of $old) {
      const index = $new.map((obj) => obj[id]).indexOf(item[id]);

      if (index === -1 || $new[index].status === Constants.OBJECT_STATUS.REMOVE) {
        output.push({
          $new: {},
          $old: item,
          title: conf.title ? conf.title(item) : AdaaHelper.getItemValueByToken(item, "name"),
          hasDiff: true,
          isRemoved: true,
        });
      }
    }

    for (const item of $new) {
      const index = $old.map((obj) => obj[id]).indexOf(item[id]);

      // NOTE: new team member added
      if (item.status === Constants.OBJECT_STATUS.REMOVE) continue;
      if (index === -1) {
        output.push({
          $new: item,
          $old: {},
          title: conf.title ? conf.title(item) : AdaaHelper.getItemValueByToken(item, "name"),
          hasDiff: true,
        });
      } else {
        // NOTE: check for any differences
        const oldItem = $old[index];
        const diffDetected = this._diffDetected(item, oldItem);

        let hasDiff = false;
        if (conf.hasDiff) {
          hasDiff = conf.hasDiff(item, oldItem);
        }

        output.push({
          title: conf.title ? conf.title(item) : AdaaHelper.getItemValueByToken(item, "name"),
          $new: item,
          $old: oldItem,
          hasDiff: this.isNew() || diffDetected || hasDiff,
        });
      }
    }

    return output;
  }

  private _diffDetected($new: WorkflowDiffListItemType, $old: WorkflowDiffListItemType) {
    const conf = this.config();
    for (const _conf of conf.config) {
      const { field } = _conf;
      const newDataField = $new?.[field];
      const oldDataField = $old?.[field];

      if (_conf.view === "accordion" || _conf.view === "targets") continue;
      if (_conf.hasDiff?.($new, $old)) {
        return true;
      } else if (
        _conf.view === "list" &&
        this._listHasDiff(_conf as ListSectionConfigType, newDataField, oldDataField)
      ) {
        return true;
      } else if (!_conf.hasDiff && _conf.view !== "list" && newDataField !== oldDataField) {
        return true;
      }
    }
    return false;
  }

  private _listHasDiff(conf: ListSectionConfigType, $new: unknown, $old: unknown): boolean {
    let newList = (Array.isArray($new) ? $new : []) as WorkflowDiffListItemType[];
    let oldList = (Array.isArray($old) ? $old : []) as WorkflowDiffListItemType[];

    if (typeof conf.derivedList === "function") {
      newList = conf.derivedList(newList) as WorkflowDiffListItemType[];
      oldList = conf.derivedList(oldList) as WorkflowDiffListItemType[];
    }

    if (newList.length !== oldList.length) return true;

    const oldTexts = new Map(oldList.map((item) => [item[conf.idKey], this._listItemText(conf, item)]));
    return newList.some((item) => {
      const id = item[conf.idKey];
      if (!oldTexts.has(id)) return true;
      return oldTexts.get(id) !== this._listItemText(conf, item);
    });
  }

  private _listItemText(conf: ListSectionConfigType, item: WorkflowDiffListItemType) {
    if (typeof conf.format === "function") return conf.format(item as never);
    return AdaaHelper.getItemValueByToken(item, "name");
  }

  private _fetchConstants() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    for (const section of this.config().config) {
      if (section.view !== "normal" && section.view !== "list") continue;

      switch ((section as ScreenSectionConfigType).field) {
        case "targetCategory":
          (section as ScreenSectionConfigType).format = (data: { targetCategory: number }) =>
            that._getTextFromProp(data.targetCategory, that.props() || []);
          break;

        case "targetTypeId":
          (section as ScreenSectionConfigType).format = (data: { targetTypeId: number }) =>
            that._getTextFromProp(data.targetTypeId, that.props() || []);
          break;

        case "publishedState":
          (section as ScreenSectionConfigType).format = (data: { publishedState: number }) =>
            that._getTextFromProp(data.publishedState, that.props() || []);
          break;

        case "actualDataAvailabilityPeriod":
          (section as ScreenSectionConfigType).format = (data: { actualDataAvailabilityPeriod: number }) =>
            that._getTextFromProp(data.actualDataAvailabilityPeriod, that.props() || []);
          break;

        case "ytpCalc":
          (section as ScreenSectionConfigType).format = (data: { ytpCalc: number }) =>
            that._getTextFromProp(data.ytpCalc, that.props() || []);
          break;

        case "trend":
          (section as ScreenSectionConfigType).format = (data: { trend: number }) =>
            that._getTextFromProp(data.trend, that.props() || []);
          break;

        case "dataType":
          (section as ScreenSectionConfigType).format = (data: { dataType: number }) =>
            that._getTextFromProp(data.dataType, that.props() || []);
          break;

        case "measurementUnit":
          (section as ScreenSectionConfigType).format = (data: { measurementUnit: number }) =>
            that._getTextFromProp(data.measurementUnit, that.props() || []);
          break;

        case "frequency":
          (section as ScreenSectionConfigType).format = (data: { frequency: number }) =>
            that._getTextFromProp(data.frequency, that.props() || []);
          break;

        case "perspective":
          (section as ScreenSectionConfigType).format = (data: { perspective: number }) =>
            that._getTextFromProp(data.perspective, that.props() || []);
          break;

        case "classification":
          (section as ScreenSectionConfigType).format = (data: { classification: number }) =>
            that._getTextFromProp(data.classification, that.props() || []);
          break;

        case "taskType":
          (section as ScreenSectionConfigType).format = (data: { taskType: number }) =>
            that._getTextFromProp(data.taskType, that.props() || []);
          break;

        default:
          break;
      }
    }
  }

  private _getTextFromProp(id: number, list: { id: number }[]) {
    const data = list.find((e) => e.id === id);
    if (data) {
      return AdaaHelper.getItemValueByToken(data, "name");
    }
    return "";
  }

  public onChildHasDiff(event: { hasDiff: boolean; parentId: number }) {
    this.childHasDiff.set(event.hasDiff);
    this.parentDiffId.set(event.parentId);
    const parentId = this.#parentId();
    if (parentId != null) {
      this.emitHasDiff.emit({ hasDiff: true, parentId });
    }
  }

  public checkChildHasDiff(confItem: WorkflowDiffListItemType) {
    if (!confItem) return false;
    if (!this.childHasDiff()) return false;
    return confItem.id === this.parentDiffId();
  }
}
